import { BoloBanner } from './Bolo.tsx';
import { AddressFlagsBanner, AddressHistoryCard } from './People.tsx';
import { PaperTransfersCard, SendTransferDialog } from './Transfers.tsx';
import { EjectmentCard } from './Ejectments.tsx';
import { DoorHangerCard } from './DoorHangers.tsx';
import { PortalThread } from './PortalThread.tsx';
import { useState, type ReactNode } from 'react';
import { formatVehicle, OUTCOME_LABELS } from '@civilos/domain';
import { api, fileToBase64 } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { AffidavitPanel } from '../shared/affidavit.tsx';
import { fmtDate, fmtDateTime, money, titleCase } from '../shared/format.ts';
import { RulesPanel } from '../shared/rules-panel.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { SlippyMap, type MapMarker } from '../shared/SlippyMap.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, Spinner, StatusBadge, useAction, useLoad } from '../shared/ui.tsx';
import { Icon } from '../shared/icons.tsx';

export function PaperDetail({ id }: { id: string }) {
  const { me, can } = useAuth();
  const { path, navigate } = useRouter();
  const d = useLoad(() => api.get(`/api/papers/${id}`), [id]);
  const justApproved = new URLSearchParams(path.split('?')[1] ?? '').get('approved');
  const meta = useLoad(() => api.get('/api/meta'));
  const act = useAction();
  const [docView, setDocView] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [sendingOut, setSendingOut] = useState(false);
  const [tab, setTab] = useState<'overview' | 'attempts' | 'custody' | 'documents' | 'audit'>('overview');

  if (d.loading && !d.data) return <Spinner />;
  if (d.error) return <ErrorBox error={d.error} />;
  const { paper: p, case: kase, person, address, customer, server, attempts, custody, documents, affidavits, dispositions, payments, evaluation, transfers } = d.data;
  const go = (fn: () => Promise<unknown>) => act.run(async () => {
    await fn();
    d.reload();
  });
  const prompt = (msg: string) => window.prompt(msg)?.trim() || null;

  const actions: ReactNode[] = [];
  if (can('papers.review') && ['RECEIVED', 'INTAKE'].includes(p.status))
    actions.push(
      <button key="rv" className="btn primary" disabled={act.busy} onClick={() => go(() => api.post(`/api/papers/${id}/review`))}>Approve intake</button>,
      <button
        key="rvn"
        className="btn primary"
        disabled={act.busy}
        title="Approve this paper and open the next one waiting for intake"
        onClick={() =>
          act.run(async () => {
            await api.post(`/api/papers/${id}/review`);
            const n = await api.get<{ next: { id: string } | null; remaining: number }>(`/api/papers/next-intake?after=${id}`);
            const done = encodeURIComponent(p.civilos_number);
            navigate(n.next ? `/papers/${n.next.id}?approved=${done}&left=${n.remaining}` : `/papers?tab=review&approved=${done}`);
          })
        }
      >
        Approve &amp; next
      </button>,
    );
  if (can('payments.record') && p.payment_status === 'PENDING' && ['INTAKE', 'REVIEWED', 'RECEIVED'].includes(p.status)) {
    actions.push(
      <button key="pay" className="btn" onClick={() => {
        const method = prompt('Payment method: cash, check or money_order');
        if (!method) return;
        const amount = prompt(`Amount received ($). Fee: ${money(p.fee_cents)}`);
        if (!amount) return;
        const reference = ['check', 'money_order'].includes(method) ? prompt('Check / money order number') : null;
        go(() => api.post(`/api/papers/${id}/payments`, { method, amountCents: Math.round(Number(amount) * 100), reference }));
      }}>Record payment</button>,
      <button key="fee" className="btn ghost" onClick={() => {
        const v = prompt('Set fee ($). Enter 0 for fee-exempt.');
        if (v !== null) go(() => api.post(`/api/papers/${id}/fee`, { feeCents: Math.round(Number(v) * 100), reason: prompt('Reason (optional)') }));
      }}>Set fee / waive</button>,
    );
  }
  if (can('payments.override') && p.payment_status === 'PENDING')
    actions.push(<button key="po" className="btn ghost" onClick={() => { const r = prompt('Reason for assigning before payment'); if (r) go(() => api.post(`/api/papers/${id}/payment-override`, { reason: r })); }}>Payment override</button>);
  if (can('papers.hold') && !['CLOSED', 'ARCHIVED'].includes(p.status))
    actions.push(
      p.on_hold ? (
        <button key="rel" className="btn" onClick={() => go(() => api.post(`/api/papers/${id}/release`))}>Release hold</button>
      ) : (
        <button key="hold" className="btn warn" onClick={() => go(() => api.post(`/api/papers/${id}/hold`, { reason: prompt('Hold reason (optional)') }))}>Place on hold</button>
      ),
    );
  if (can('papers.mark_urgent') && ['READY', 'ASSIGNED', 'IN_SERVICE'].includes(p.status))
    actions.push(<button key="urg" className="btn ghost" onClick={() => go(() => api.post(`/api/papers/${id}/urgency`, { urgent: !p.manual_urgent }))}>{p.manual_urgent ? 'Remove urgent' : 'Mark urgent'}</button>);
  if (can('attempts.calendar_override') && ['ASSIGNED', 'IN_SERVICE'].includes(p.status))
    actions.push(<button key="cal" className="btn ghost" onClick={() => { const dt = prompt('Authorize Sunday/holiday attempt on date (YYYY-MM-DD)'); if (dt) go(() => api.post(`/api/papers/${id}/calendar-override`, { date: dt })); }}>Sunday/holiday override</button>);
  if (can('papers.close') && ['AFFIDAVIT_SIGNED', 'RETURNED'].includes(p.status))
    actions.push(<button key="close" className="btn" onClick={() => go(() => api.post(`/api/papers/${id}/close`))}>Close paper</button>);
  if (can('papers.hold') && !p.recalled && ['INTAKE', 'REVIEWED', 'READY', 'ASSIGNED', 'IN_SERVICE'].includes(p.status))
    actions.push(<button key="rec" className="btn ghost" onClick={() => { const r = prompt('Recall / cancel reason (customer request)'); if (r) go(() => api.post(`/api/papers/${id}/recall`, { reason: r })); }}>Recall</button>);

  const openAgencyTransfer = (d.data.agencyTransfers ?? []).some((t: any) => ['sent', 'accepted', 'returned'].includes(t.status) && t.direction === 'outgoing');
  if (can('papers.review') && ['RECEIVED', 'INTAKE', 'REVIEWED', 'READY'].includes(p.status) && !openAgencyTransfer && !p.recalled)
    actions.push(<button key="xfer" className="btn ghost" onClick={() => setSendingOut(true)}>Send to another county</button>);

  const flaggedAttempt = attempts.find((x: any) => x.review_status === 'needs_review');

  return (
    <div className="stack">
      {sendingOut && <SendTransferDialog paper={p} onClose={() => setSendingOut(false)} onSent={() => { setSendingOut(false); d.reload(); }} />}
      <header className="rec-head">
        <div className="crumb">
          <Link to="/papers">Papers</Link> ›
        </div>
        <div className="rec-title">
          <h1>
            <span className="mono">{p.civilos_number}</span> <span className="rec-sep">—</span> {evaluation.paperType.name}
          </h1>
          <StatusBadge status={p.status} labels={me!.agency.statusLabels} />
          {p.on_hold && <Badge tone="bad">On hold{p.hold_reason ? `: ${p.hold_reason}` : ''}</Badge>}
          {p.recalled && <Badge tone="bad">Recalled</Badge>}
          {p.iv_d && <Badge tone="info">IV-D</Badge>}
          <Badge tone={p.payment_status === 'PENDING' ? 'warn' : 'ok'}>Payment {p.payment_status.toLowerCase()}</Badge>
          <div className="rec-tools">
            <img src={`/api/papers/${id}/barcode.svg`} alt={`Barcode ${p.civilos_number}`} height={40} className="barcode" />
            <Link to={`/papers/print?kind=labels&ids=${id}`} className="btn sm">
              <Icon name="print" size={15} /> Print label
            </Link>
            <Link to={`/papers/print?kind=covers&ids=${id}`} className="btn sm">
              Cover sheet
            </Link>
            {affidavits.some((a: any) => a.status === 'signed') && (
              <a className="btn sm" href={`/api/papers/${id}/package.pdf`}>
                Final package PDF
              </a>
            )}
          </div>
        </div>
        <dl className="rec-meta">
          <div>
            <dt>Defendant</dt>
            <dd>{person.full_name}</dd>
          </div>
          <div>
            <dt>Case</dt>
            <dd className="mono">{kase.case_number}</dd>
          </div>
          <div>
            <dt>Court date</dt>
            <dd>{p.court_date ? fmtDate(p.court_date) : '—'}</dd>
          </div>
          <div>
            <dt>Customer</dt>
            <dd>{customer?.name ?? '—'}</dd>
          </div>
          <div>
            <dt>Deputy</dt>
            <dd>{server?.display_name ?? server?.name ?? 'Not assigned'}</dd>
          </div>
          <div>
            <dt>Fee</dt>
            <dd>{money(p.fee_cents)}</dd>
          </div>
        </dl>
      </header>

      <BoloBanner hits={d.data.bolos ?? []} />
      <AddressFlagsBanner flags={d.data.addressHistory?.flags ?? []} />
      {justApproved && (
        <Notice tone="ok">
          ✓ Approved {justApproved}. {(() => {
            const left = Number(new URLSearchParams(path.split('?')[1] ?? '').get('left') ?? 0);
            return left > 1 ? `${left} papers waiting for intake, including this one.` : 'This is the last paper waiting for intake.';
          })()}
        </Notice>
      )}
      {actions.length > 0 && (
        <div className="toolbar" role="toolbar" aria-label="Paper actions">
          {actions}
        </div>
      )}
      <ErrorBox error={act.error} />

      {p.status === 'PENDING_REVIEW' && can('dispositions.review') && (
        <Card title="Supervisor review required">
          {flaggedAttempt ? (
            <div className="stack">
              <Notice tone="warn">
                GPS anomaly: {flaggedAttempt.anomaly}
                <br />
                Server explanation: “{flaggedAttempt.server_explanation}”
              </Notice>
              <div className="row gap">
                <button className="btn primary" onClick={() => go(() => api.post(`/api/attempts/${flaggedAttempt.id}/review`, { decision: 'approved', note: prompt('Approval note (optional)') }))}>Approve attempt</button>
                <button className="btn warn" onClick={() => { const n = prompt('Rejection reason'); if (n) go(() => api.post(`/api/attempts/${flaggedAttempt.id}/review`, { decision: 'rejected', note: n })); }}>Reject attempt</button>
              </div>
            </div>
          ) : (
            <div className="stack">
              <p className="muted">{dispositions.at(-1)?.reason ?? 'Pending review'}</p>
              <div className="row gap wrap">
                {(['non_served', 'served', 'return_to_service'] as const).map((dec) => (
                  <button key={dec} className="btn" onClick={() => { const r = prompt(`Reason / action taken for "${titleCase(dec)}"`); if (r) go(() => api.post(`/api/papers/${id}/review-decision`, { decision: dec, reason: r })); }}>
                    {dec === 'non_served' ? 'Finalize non-service' : dec === 'served' ? 'Mark completed (action taken)' : 'Return to service'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="tabs" role="tablist">
        {(['overview', 'attempts', 'custody', 'documents', 'audit'] as const)
          .filter((t) => t !== 'audit' || can('audit.read'))
          .map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
              {titleCase(t)}
              {t === 'attempts' ? ` (${attempts.length})` : t === 'documents' ? ` (${documents.length})` : ''}
            </button>
          ))}
      </div>

      {tab === 'overview' && (
        <div className="grid2 top">
          <div className="stack">
            {editing ? (
              <EditServiceDetails d={d.data} meta={meta.data} onCancel={() => setEditing(false)} onSaved={() => (setEditing(false), d.reload())} />
            ) : (
            <Card
              title="Service details"
              actions={
                can('papers.intake') && EDITABLE.includes(p.status) ? (
                  <button className="btn sm" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                ) : undefined
              }
            >
              <dl className="dl">
                <dt>Defendant</dt><dd>{person.full_name} {d.data.personKey && <Link to={`/people/profile?key=${encodeURIComponent(d.data.personKey)}`} className="small">Person profile</Link>}</dd>
                {person.phone && (<><dt>Phone</dt><dd>{person.phone}</dd></>)}
                <dt>Address</dt>
                <dd>
                  {[address.line1, address.line2, address.city, address.state, address.zip].filter(Boolean).join(', ')}
                  {address.pin_lat != null && <Badge tone="info">corrected pin</Badge>}
                </dd>
                <dt>Case</dt><dd className="mono">{kase.case_number}</dd>
                <dt>Court</dt><dd>{kase.court_name ?? '—'}</dd>
                <dt>Plaintiff</dt><dd>{kase.plaintiff ?? '—'}{kase.plaintiff_phone && <span className="muted"> · <a href={`tel:${kase.plaintiff_phone.replace(/[^\d+]/g, '')}`}>{kase.plaintiff_phone}</a></span>}</dd>
                {kase.attorney && (<><dt>Attorney</dt><dd>{kase.attorney}</dd></>)}
                <dt>Court date</dt><dd>{fmtDate(p.court_date)} {p.court_time ?? ''}</dd>
                <dt>Customer</dt><dd>{customer ? `${customer.name} (${customer.customer_class})` : '—'}</dd>
                <dt>Received</dt><dd>{fmtDateTime(p.received_at)}</dd>
                <dt>Fee</dt><dd>{money(p.fee_cents)} {payments.length > 0 && <span className="muted small">receipts {payments.map((x: any) => `#${x.receipt_number}`).join(', ')}</span>}</dd>
                <dt>Assigned</dt><dd>{server ? `${server.display_name} (${titleCase(p.assignment_mode ?? '')})` : '—'}</dd>
                <dt>Custody</dt><dd>{titleCase(p.custody_holder_type)}</dd>
                {person.vehicle_info && (<><dt>Vehicle</dt><dd>{person.vehicle_info}</dd></>)}
                {person.employer && (<><dt>Employer</dt><dd>{person.employer}{person.employer_address ? ` — ${person.employer_address}` : ''}</dd></>)}
                <dt>IV-D</dt><dd>{p.iv_d ? 'Yes — child support' : 'No'}</dd>
                {p.special_instructions && (<><dt>Instructions</dt><dd>{p.special_instructions}</dd></>)}
              </dl>
            </Card>
            )}
            <PaperTransfersCard transfers={d.data.agencyTransfers ?? []} />
            <PortalThread paperId={id} customerName={customer?.name ?? null} />
            <DoorHangerCard responses={d.data.doorHangerResponses ?? []} />
            <AddressHistoryCard history={d.data.addressHistory} />
            <LocationCard paperId={id} address={address} version={p.version} onChanged={d.reload} />
            {can('custody.authorize_transfer') && p.custody_holder_type === 'server' && (
              <TransferCard paperId={id} currentServer={p.custody_holder_id} servers={meta.data?.servers ?? []} transfers={transfers} onDone={d.reload} />
            )}
            {p.paper_type_code === 'WRIT_OF_EJECTMENT' && (
              <EjectmentCard paper={{ id, defendant: person?.full_name, address: [address?.line1, address?.city].filter(Boolean).join(', '), plaintiff: kase?.plaintiff }} setOuts={d.data.setOuts ?? []} servedAt={p.served_at} onChanged={d.reload} />
            )}
          </div>
          <div className="stack">
            <Card title="Rules engine">
              <RulesPanel ev={evaluation} />
            </Card>
            <AffidavitPanel paperId={id} paperStatus={p.status} affidavits={affidavits} onChanged={d.reload} />
            {can('dispositions.override') && ['ASSIGNED', 'IN_SERVICE'].includes(p.status) && (
              <Card title="Non-service">
                <div className="row gap wrap">
                  {evaluation.nonService.satisfied && evaluation.nonService.basis === 'attempts' && (
                    <button className="btn" onClick={() => go(() => api.post(`/api/papers/${id}/non-service`))}>Finalize non-service</button>
                  )}
                  <button className="btn ghost" onClick={() => { const r = prompt('Written reason for supervisor non-service override (required)'); if (r) go(() => api.post(`/api/papers/${id}/non-service`, { override: true, reason: r })); }}>
                    Supervisor override…
                  </button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {tab === 'attempts' && (
        <Card>
          {attempts.length === 0 ? <Empty>No attempts yet.</Empty> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>When</th><th>Outcome</th><th>Method</th><th>Server</th><th>GPS</th><th>Source</th><th>Notes</th></tr></thead>
                <tbody>
                  {attempts.map((x: any) => (
                    <tr key={x.id}>
                      <td>{fmtDateTime(x.attempted_at)}</td>
                      <td>
                        {(OUTCOME_LABELS as any)[x.outcome]} {x.review_status === 'needs_review' && <Badge tone="bad">needs review</Badge>}
                        {x.review_status === 'rejected' && <Badge tone="bad">rejected</Badge>}
                        {x.evaluation.outsideServiceHours && <Badge tone="warn">outside hours</Badge>}
                      </td>
                      <td>{x.method ?? '—'}{x.recipient?.name ? ` → ${x.recipient.name} (${x.recipient.relationship})` : ''}{x.door_hanger ? ' · door hanger left' : ''}
                        {(x.vehicles ?? []).map((v: any, k: number) => (
                          <div key={k} className="small muted">
                            Vehicle: {formatVehicle(v)}{' '}
                            {v.photoDocumentId && (
                              <a href={`/api/documents/${v.photoDocumentId}/content`} target="_blank" rel="noreferrer">
                                photo
                              </a>
                            )}
                          </div>
                        ))}
                      </td>
                      <td>{x.server_name}</td>
                      <td>{x.distance_ft != null ? <span className={x.outside_radius ? 'text-warn' : ''}>{x.distance_ft.toLocaleString()} ft</span> : '—'}</td>
                      <td>{x.source}</td>
                      <td className="muted">{[x.notes, x.outside_hours_explanation].filter(Boolean).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'custody' && (
        <Card title="Chain of custody">
          {custody.length === 0 ? <Empty>Paper has been in office custody since intake.</Empty> : (
            <ol className="timeline">
              {custody.map((c: any) => (
                <li key={c.id}>
                  <strong>{titleCase(c.action)}</strong> — {titleCase(c.from_type)}{c.from_name ? ` (${c.from_name})` : ''} → {titleCase(c.to_type)}{c.to_name ? ` (${c.to_name})` : ''}
                  <div className="muted small">
                    {fmtDateTime(c.created_at)} · by {c.performed_by_name} · {c.scanned ? 'barcode scan' : 'manual entry'}
                    {c.note ? ` · ${c.note}` : ''}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {can('custody.mark_lost') && p.custody_holder_type !== 'lost' && (
            <button className="btn ghost sm" onClick={() => { const r = prompt('Explanation for marking this paper lost'); if (r) go(() => api.post(`/api/papers/${id}/mark-lost`, { explanation: r })); }}>
              Mark lost…
            </button>
          )}
        </Card>
      )}

      {tab === 'documents' && (
        <Card title="Documents" actions={can('documents.upload') && <UploadButton paperId={id} onDone={d.reload} />}>
          {documents.length === 0 ? <Empty>No documents.</Empty> : (
            <ul className="plain">
              {documents.map((doc: any) => (
                <li key={doc.id} className="row gap wrap">
                  <Badge tone={doc.kind === 'intake_original' ? 'info' : 'neutral'}>{titleCase(doc.kind)}</Badge>
                  <span>{doc.filename}</span>
                  <span className="muted small">{(doc.size_bytes / 1024).toFixed(0)} KB · {fmtDateTime(doc.created_at)} · sha256 {doc.sha256.slice(0, 10)}…</span>
                  <button className="btn sm" onClick={() => setDocView(doc.id)}>View</button>
                </li>
              ))}
            </ul>
          )}
          {docView && (
            <div className="viewer">
              <div className="row between"><strong>Document viewer</strong><button className="btn sm ghost" onClick={() => setDocView(null)}>Close</button></div>
              <iframe title="Document" src={`/api/documents/${docView}/content`} />
            </div>
          )}
        </Card>
      )}

      {tab === 'audit' && <AuditTab paperId={id} />}
    </div>
  );
}

function UploadButton({ paperId, onDone }: { paperId: string; onDone: () => void }) {
  const a = useAction();
  return (
    <label className="btn sm">
      {a.busy ? 'Uploading…' : 'Add document'}
      <input
        type="file"
        hidden
        accept="application/pdf,image/png,image/jpeg,image/tiff"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) a.run(async () => {
            await api.post(`/api/papers/${paperId}/documents`, { filename: f.name, dataBase64: await fileToBase64(f) });
            onDone();
          });
        }}
      />
      {a.error ? <ErrorBox error={a.error} /> : null}
    </label>
  );
}

function TransferCard({ paperId, currentServer, servers, transfers, onDone }: { paperId: string; currentServer: string; servers: any[]; transfers: any[]; onDone: () => void }) {
  const [to, setTo] = useState('');
  const a = useAction();
  const pending = transfers.find((t) => ['authorized', 'in_transit'].includes(t.status));
  return (
    <Card title="Direct field transfer">
      {pending ? (
        <Notice tone="warn">Transfer {pending.status.replace('_', ' ')}: {pending.from_name} → {pending.to_name} (authorized {fmtDateTime(pending.authorized_at)}). Sending server stays custodian until the receiving server scans in.</Notice>
      ) : (
        <form className="row gap wrap" onSubmit={(e) => { e.preventDefault(); a.run(async () => { await api.post('/api/custody/transfers', { paperId, toServerId: to }); onDone(); }); }}>
          <select value={to} onChange={(e) => setTo(e.target.value)} required>
            <option value="">Transfer to server…</option>
            {servers.filter((s) => s.id !== currentServer).map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
          </select>
          <button className="btn" disabled={a.busy}>Authorize transfer</button>
        </form>
      )}
      <ErrorBox error={a.error} />
    </Card>
  );
}

function AuditTab({ paperId }: { paperId: string }) {
  const r = useLoad(() => api.get(`/api/papers/${paperId}/audit`), [paperId]);
  if (r.loading) return <Spinner />;
  return (
    <Card title="Audit history (immutable)">
      <ErrorBox error={r.error} />
      <div className="table-wrap">
        <table className="table small">
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Details</th></tr></thead>
          <tbody>
            {r.data?.events.map((e: any) => (
              <tr key={e.id}>
                <td>{fmtDateTime(e.at)}</td>
                <td>{e.actor ?? 'system'}</td>
                <td className="mono">{e.action}</td>
                <td className="muted mono wrap-any">{Object.keys(e.details ?? {}).length ? JSON.stringify(e.details) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Paper location (ROUTE-012): geocoded / corrected pin, attempt GPS points, and pin correction by clicking the map. */
function LocationCard({ paperId, address, version, onChanged }: { paperId: string; address: any; version: number; onChanged: () => void }) {
  const { can } = useAuth();
  const pts = useLoad(() => api.get(`/api/papers/${paperId}/map`), [paperId, version, address.pin_lat, address.lat]);
  const act = useAction();
  const [placing, setPlacing] = useState<{ lat: number; lng: number } | null | 'armed'>(null);
  const pin = address.pin_lat != null ? { lat: address.pin_lat, lng: address.pin_lng } : address.lat != null ? { lat: address.lat, lng: address.lng } : null;
  const markers: MapMarker[] = [];
  if (pin) markers.push({ id: 'address', ...pin, color: '#14213d', title: `Service address${address.pin_lat != null ? ' (corrected pin)' : ''}` });
  (pts.data?.attempts ?? []).forEach((a: any, i: number) =>
    markers.push({ id: `a${a.id}`, lat: a.gps_lat, lng: a.gps_lng, dot: true, color: a.outside_radius ? '#c9362b' : '#1f9d55', title: `Attempt ${i + 1}: ${OUTCOME_LABELS[a.outcome as keyof typeof OUTCOME_LABELS] ?? a.outcome}, ${fmtDateTime(a.attempted_at)}${a.distance_ft != null ? `, ${a.distance_ft} ft from pin` : ''}` }),
  );
  if (placing && placing !== 'armed') markers.push({ id: 'new', ...placing, color: '#fca311', title: 'New pin (not saved)', selected: true });
  const status: Record<string, string> = {
    matched: address.geocode_source === 'county' ? 'On the house — county address points' : 'Located by U.S. Census geocoder',
    manual: 'Coordinates entered at intake',
    pending: 'Looking up address…',
    no_match: 'Address not found by the geocoder',
    failed: 'Geocoder was unavailable',
    skipped: 'Geocoding is turned off',
  };
  const canPin = can('papers.intake');
  const save = () =>
    act.run(async () => {
      if (!placing || placing === 'armed') return;
      await api.post(`/api/papers/${paperId}/pin`, { lat: Math.round(placing.lat * 1e6) / 1e6, lng: Math.round(placing.lng * 1e6) / 1e6 });
      setPlacing(null);
      onChanged();
    });
  return (
    <Card
      title="Location"
      actions={
        canPin && (
          <div className="row gap-sm">
            {placing ? (
              <>
                <button className="btn sm primary" disabled={placing === 'armed' || act.busy} onClick={save}>
                  Save pin
                </button>
                <button className="btn sm ghost" onClick={() => setPlacing(null)}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn sm" onClick={() => setPlacing('armed')}>
                Set pin on map
              </button>
            )}
          </div>
        )
      }
    >
      <div className="stack sm">
        {placing && <Notice tone="info">Click the exact spot on the map (the door or driveway), then Save pin. Satellite view helps.</Notice>}
        <SlippyMap
          height={300}
          fitKey={`${paperId}|${pin?.lat}|${pin?.lng}|${pts.data?.attempts?.length ?? 0}`}
          ariaLabel="Paper location map"
          markers={markers}
          crosshair={!!placing}
          onMapClick={placing ? (p) => setPlacing(p) : undefined}
        />
        <div className="row gap wrap small">
          <span className="muted">{address.pin_lat != null ? 'Corrected pin (used for navigation and GPS radius)' : status[address.geocode_status] ?? ''}</span>
          {address.matched_address && address.pin_lat == null && <span className="muted">· matched “{address.matched_address}”</span>}
          {(pts.data?.attempts ?? []).length > 0 && (
            <span className="row gap-sm">
              <span className="legend-dot" style={{ background: '#1f9d55' }} /> attempt within radius
              <span className="legend-dot" style={{ background: '#c9362b' }} /> outside radius
            </span>
          )}
        </div>
        <div className="row gap wrap">
          {canPin && ['no_match', 'failed', 'skipped', 'pending'].includes(address.geocode_status) && address.pin_lat == null && (
            <button className="btn sm" disabled={act.busy} onClick={() => act.run(async () => (await api.post(`/api/papers/${paperId}/geocode`), onChanged()))}>
              Look up address again
            </button>
          )}
          {can('papers.hold') && address.pin_lat != null && (
            <button className="btn sm ghost" disabled={act.busy} onClick={() => act.run(async () => (await api.post(`/api/papers/${paperId}/pin`, { reset: true }), onChanged()))}>
              Reset to geocoded location
            </button>
          )}
        </div>
        <ErrorBox error={act.error} />
      </div>
    </Card>
  );
}

const EDITABLE = ['RECEIVED', 'INTAKE', 'REVIEWED', 'READY', 'ASSIGNED', 'IN_SERVICE', 'PENDING_REVIEW'];
const IN_FIELD = ['ASSIGNED', 'IN_SERVICE', 'PENDING_REVIEW'];
const TYPE_EDITABLE = ['RECEIVED', 'INTAKE', 'REVIEWED'];

/** Clerk edits service details in place. Only changed fields are sent; the server audits before → after. */
function EditServiceDetails({ d, meta, onCancel, onSaved }: { d: any; meta: any; onCancel: () => void; onSaved: () => void }) {
  const { paper: p, case: kase, person, address, payments } = d;
  const init = {
    defendantName: person.full_name ?? '',
    dob: person.dob ? String(person.dob).slice(0, 10) : '',
    employer: person.employer ?? '',
    employerAddress: person.employer_address ?? '',
    vehicle: person.vehicle_info ?? '',
    phone: person.phone ?? '',
    line1: address.line1 ?? '',
    line2: address.line2 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? '',
    caseNumber: kase.case_number ?? '',
    plaintiff: kase.plaintiff ?? '',
    plaintiffPhone: kase.plaintiff_phone ?? '',
    attorney: kase.attorney ?? '',
    courtId: kase.court_id ?? '',
    courtDate: p.court_date ? String(p.court_date).slice(0, 10) : '',
    courtTime: p.court_time ?? '',
    customerId: p.customer_id ?? '',
    paperTypeCode: p.paper_type_code,
    specialInstructions: p.special_instructions ?? '',
  };
  const [ivD, setIvD] = useState<boolean>(!!p.iv_d);
  const [f, setF] = useState(init);
  const [reason, setReason] = useState('');
  const a = useAction();
  const set = (k: keyof typeof init) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const inField = IN_FIELD.includes(p.status);
  const typeLocked = !TYPE_EDITABLE.includes(p.status) || payments.length > 0;
  const addrKeys = ['line1', 'line2', 'city', 'state', 'zip'] as const;
  const addrChanged = addrKeys.some((k) => f[k] !== init[k]);
  const caseChanged = f.caseNumber !== init.caseNumber || f.plaintiff !== init.plaintiff || f.plaintiffPhone !== init.plaintiffPhone || f.courtId !== init.courtId || f.attorney !== init.attorney;
  const body: Record<string, unknown> = {};
  for (const k of ['defendantName', 'dob', 'employer', 'employerAddress', 'vehicle', 'phone', 'caseNumber', 'plaintiff', 'plaintiffPhone', 'attorney', 'courtId', 'courtDate', 'courtTime', 'customerId', 'paperTypeCode', 'specialInstructions'] as const)
    if (f[k] !== init[k]) body[k] = f[k] === '' ? null : f[k];
  if (ivD !== !!p.iv_d) body.ivD = ivD;
  if (addrChanged) body.address = { line1: f.line1, line2: f.line2 || null, city: f.city || null, state: f.state || null, zip: f.zip || null };
  const dirty = Object.keys(body).length > 0;
  const save = () =>
    a.run(async () => {
      await api.patch(`/api/papers/${p.id}`, { ...body, reason: reason.trim() || undefined });
      onSaved();
    });
  return (
    <Card title="Edit service details">
      <form
        data-unsaved={dirty ? 'true' : undefined}
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {inField && <Notice tone="warn">This paper is with {d.server?.display_name ?? 'a server'}. Changes reach their device on the next sync, and a reason is required.</Notice>}
        <div className="edit-grid">
          <Field label="Defendant name"><input value={f.defendantName} onChange={set('defendantName')} required /></Field>
          <Field label="Date of birth"><input type="date" value={f.dob} onChange={set('dob')} /></Field>
        </div>
        <div className="edit-grid">
          <Field label="Vehicle"><input value={f.vehicle} onChange={set('vehicle')} placeholder="e.g. Black 2015 Silverado, SC ABC123" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={set('phone')} inputMode="tel" /></Field>
          <Field label="Employer"><input value={f.employer} onChange={set('employer')} /></Field>
          <Field label="Employer address / hours"><input value={f.employerAddress} onChange={set('employerAddress')} /></Field>
        </div>
        <div className="edit-grid addr">
          <Field label="Street address"><input value={f.line1} onChange={set('line1')} required /></Field>
          <Field label="Apt / unit"><input value={f.line2} onChange={set('line2')} /></Field>
          <Field label="City"><input value={f.city} onChange={set('city')} /></Field>
          <Field label="State"><input value={f.state} onChange={set('state')} maxLength={2} style={{ width: 70 }} /></Field>
          <Field label="ZIP"><input value={f.zip} onChange={set('zip')} inputMode="numeric" maxLength={10} style={{ width: 110 }} /></Field>
        </div>
        {addrChanged && <p className="small muted">A new street, city, state or ZIP clears the map pin; the address is looked up again automatically.</p>}
        <div className="edit-grid">
          <Field label="Case number"><input value={f.caseNumber} onChange={set('caseNumber')} required /></Field>
          <Field label="Plaintiff"><input value={f.plaintiff} onChange={set('plaintiff')} /></Field>
          <Field label="Plaintiff phone"><input type="tel" value={f.plaintiffPhone} onChange={set('plaintiffPhone')} /></Field>
          <Field label="Attorney for plaintiff"><input value={f.attorney} onChange={set('attorney')} /></Field>
          <Field label="Court">
            <select value={f.courtId} onChange={set('courtId')}>
              <option value="">—</option>
              {(meta?.courts ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>
        {caseChanged && <p className="small muted">Case number, plaintiff and court belong to the case, so they change for every defendant’s paper on this case.</p>}
        <div className="edit-grid">
          <Field label="Court date"><input type="date" value={f.courtDate} onChange={set('courtDate')} /></Field>
          <Field label="Court time"><input type="time" value={f.courtTime} onChange={set('courtTime')} /></Field>
          <Field label="Paper type" hint={typeLocked ? 'Locked after the paper is ready or paid' : undefined}>
            <select value={f.paperTypeCode} onChange={set('paperTypeCode')} disabled={typeLocked}>
              {(meta?.paperTypes ?? []).map((t: any) => (
                <option key={t.code} value={t.code}>{t.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Customer" hint={typeLocked ? 'Locked after the paper is ready or paid' : 'Changing it re-prices the fee'}>
            <select value={f.customerId} onChange={set('customerId')} disabled={typeLocked}>
              <option value="">—</option>
              {(meta?.customers ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Special instructions"><textarea rows={2} value={f.specialInstructions} onChange={set('specialInstructions')} /></Field>
        <label className="row gap-sm ivd-check">
          <input type="checkbox" checked={ivD} onChange={(e) => setIvD(e.target.checked)} /> IV-D (child support) paper
        </label>
        <Field label={inField ? 'Reason for the change (required)' : 'Reason (optional)'}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Court sent corrected address" required={inField && dirty} />
        </Field>
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={!dirty || a.busy || (inField && !reason.trim())}>{a.busy ? 'Saving…' : 'Save changes'}</button>
          <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </Card>
  );
}
